import React, { createContext, useContext, startTransition } from "react";
import { useAuthState } from "./auth/useAuthState";
import { useAuthUtils } from "./auth/useAuthUtils";
import { login as apiLogin } from "@/services/authService";
import { permissionEngine } from "@/services/auth/permissionEngine";
import { userActivityApi } from "@/services/api/userActivityApi";
import type { AuthContextType, User } from "./auth/types";

// Define the API response user structure
interface ApiUser {
  id: number;
  email: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  is_active?: boolean;
  is_superuser?: boolean;
}

// Export the AuthContext so it can be imported by other files
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, setUser, isLoading, navigate, toast } = useAuthState();
  const { hasPermission: checkPermission, hasRole: checkRole } = useAuthUtils();

  const login = async (email: string, password: string) => {
    try {
      console.log('🔑 Attempting to login with email:', email);
      const response = await apiLogin(email, password);
      
      if (!response || !response.user) {
        throw new Error('Invalid response from server');
      }
      
      // Transform the API response to match our User type
      const apiUser = response.user as ApiUser;
      const userData: User = {
        id: apiUser.id,
        email: apiUser.email,
        username: apiUser.username,
        firstName: apiUser.first_name || undefined,
        lastName: apiUser.last_name || undefined,
        role: apiUser.is_superuser ? "admin" : "user",
        permissions: [],
        isActive: apiUser.is_active,
        isSuperuser: apiUser.is_superuser
      };
      
      // Store the token for future authenticated requests
      localStorage.setItem("token", response.token);
      localStorage.setItem("loginJustCompleted", "true");
      
      // Clear any existing permission cache before setting new user
      permissionEngine.clearAllCache();
      
      // Use startTransition for auth state updates
      startTransition(() => {
        setUser(userData);
        localStorage.setItem("user", JSON.stringify(userData));
      });
      
      console.log('✅ Login successful, user data set:', userData);
      
      // Build enhanced user shape for permission engine
      const enhancedUser = {
        ...userData,
        username: userData.username || userData.email,
        first_name: userData.firstName || '',
        last_name: userData.lastName || '',
        is_active: userData.isActive || false,
        is_superuser: userData.isSuperuser || false,
        permissions: userData.permissions.map(permString => ({
          id: permString,
          name: permString,
          resource_id: permString.split('.')[0] || permString,
          action_id: permString.split('.')[1] || 'general'
        }))
      };

      if (userData.isSuperuser) {
        console.log('👑 Superuser login detected, setting up permissions immediately');
        permissionEngine.setupSuperuserPermissions(enhancedUser);
      } else {
        // Kick off permission preload immediately after login so UI isn't empty
        console.log('🚀 Preloading permissions for user on login:', enhancedUser.id);
        permissionEngine.preloadUserPermissions(enhancedUser).catch((error) => {
          console.error('❌ Permission preload on login failed:', error);
        });
      }
      
      // Intelligent routing: check for intended destination or use sensible default
      const currentLocation = window.location;
      const urlParams = new URLSearchParams(currentLocation.search);
      const redirectTo = urlParams.get('redirect');
      
      // Navigate to intended destination or appropriate dashboard based on role
      if (redirectTo) {
        navigate(redirectTo);
      } else if (userData.isSuperuser || userData.role === 'admin') {
        navigate('/app/dashboard'); // Admin Dashboard
      } else {
        navigate('/app/home'); // User Dashboard
      }
      
      toast({
        title: "Login successful",
        description: `Welcome back, ${userData.email}`,
      });
    } catch (error) {
      console.error("❌ Login error:", error);
      // Don't show toast error message - let the login component handle the UI state
      // This prevents the "Incorrect username or password" message from appearing
      throw error; // Re-throw so Login component can handle it
    }
  };

  const logout = async () => {
    // Record logout activity before clearing auth
    try {
      await userActivityApi.logout();
    } catch {}

    // Clear permission cache on logout
    permissionEngine.clearAllCache();
    
    startTransition(() => {
      setUser(null);
      localStorage.removeItem("user");
      localStorage.removeItem("token");
    });
    navigate("/login");
    
    toast({
      title: "Logged out successfully",
      description: "You have been logged out of your account",
    });
  };

  const hasPermission = (permission: string): boolean => {
    if (!user) return false;
    return checkPermission(user, permission);
  };
  
  const hasRole = (roles: User['role'] | User['role'][]): boolean => {
    if (!user) return false;
    return checkRole(user, roles);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      isLoading, 
      login, 
      logout, 
      hasPermission, 
      hasRole,
      isAuthenticated: !!user 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
